import { MainContent } from '../../shared-mui-components/MainContent.tsx';
import { Localized } from 'dg-web-shared/lib/Localization.tsx';
import { OperatorAsyncLoadedSection } from '../../app/components/OperatorAsyncLoadedSection.tsx';
import { LoadingSpinnerPresets } from 'dg-web-shared/common/components/material-ui/PresetLoadingSpinner.tsx';
import { EmptyResults } from '../../shared-mui-components/ListMessages.tsx';
import {
    Button,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from '@mui/material';
import {
    AccountBalanceWallet,
    CloudDownload,
    PictureAsPdf,
} from '@mui/icons-material';
import {
    RequestStatus,
    useServerFetch,
} from 'dg-web-shared/lib/hooks/ServerStateHooks.ts';
import { SeparationBar } from '../../layout/components/SeparationBar.tsx';
import { Formatter } from 'dg-web-shared/lib/Date.ts';
import { HeaderWithButtons } from '../../shared-mui-components/HeaderWithButtons.tsx';
import { useNavigate, useParams } from 'react-router-dom';
import { currencyCentsToLocalPrice } from 'dg-web-shared/lib/NumberFormatter.ts';
import { ButtonWithDropdown } from '../../layout/components/ButtonWithDropdown.tsx';
import { openNewTab } from 'dg-web-shared/common/utils/Navigation.tsx';
import { urlWithOperatorExternalId } from '../../utils/Utils.ts';
import { useOperatorContext } from '../../app/components/BaseLayoutAndData.tsx';

interface PayStationCollectionData {
    hasCashMovementsReport: boolean;
    collections: PayStationCollection[];
}

interface PayStationCollection {
    collectionId: string;
    collectionNr: string;
    collectedAt: string;
    zoneName: string;
    payStationName: string | null;
    onPremiseId: number;
    transactionsCount: number;
    collectedAmountRappen: number;
    coinAmountRappen: number;
    banknoteAmountRappen: number;
    hasPdfReport: boolean;
}

export function PayStationCollections() {
    const { operatorExternalId } = useOperatorContext();
    const { year, month } = useParams();

    const [payStationCollectionState] = useServerFetch<
        PayStationCollectionData,
        { year: string; month: string }
    >(
        ({ year, month }) => ({
            url: `/ui-api/operator-account/pay-station/collections/${year}/${month}`,
        }),
        year && month ? { year: year, month: month } : null,
    );
    const navigate = useNavigate();

    return (
        <MainContent>
            <HeaderWithButtons
                icon={AccountBalanceWallet}
                title={
                    <Localized
                        de={`Kassen-Leerungen | ${month}-${year}`}
                        fr={`Collectes des caisses | ${month}-${year}`}
                        it={`Vuotature casse | ${month}-${year}`}
                        en={`Pay stations colletions | ${month}-${year}`}
                    />
                }
                headerButtons={
                    <Stack
                        direction="row"
                        spacing={3}
                        sx={{
                            alignItems: 'center',
                        }}
                    >
                        <ButtonWithDropdown
                            label={
                                <Localized
                                    de="Exportieren"
                                    fr="Exporter"
                                    it="Esporta"
                                    en="Export"
                                />
                            }
                            itemVariant={'action'}
                            variant="outlined"
                            startIcon={<CloudDownload />}
                            dropdownItems={[
                                {
                                    key: 'collection-export',
                                    label: (
                                        <Localized
                                            de="Übersicht (PDF)"
                                            fr="Aperçu (PDF)"
                                            it="Panoramica (PDF)"
                                            en="Overview (PDF)"
                                        />
                                    ),
                                    disabled:
                                        payStationCollectionState.status ===
                                            RequestStatus.PENDING ||
                                        payStationCollectionState.status ===
                                            RequestStatus.ERROR ||
                                        (payStationCollectionState.status ===
                                            RequestStatus.SUCCESS &&
                                            payStationCollectionState.data
                                                .collections.length < 1),
                                    onClickCallback: () => {
                                        openNewTab(
                                            urlWithOperatorExternalId(
                                                `/ui-api/operator-account/pay-station/collections/${year}/${month}/pdf`,
                                                operatorExternalId,
                                            ),
                                        );
                                    },
                                },
                                {
                                    key: 'cash-transactions',
                                    label: (
                                        <Localized
                                            de="Bewegungsdetails (CSV)"
                                            fr="Détail des mouvements (CSV)"
                                            it="Dettaglio movimenti (CSV)"
                                            en="Movements details (CSV)"
                                        />
                                    ),
                                    disabled:
                                        payStationCollectionState.status ===
                                            RequestStatus.PENDING ||
                                        payStationCollectionState.status ===
                                            RequestStatus.ERROR ||
                                        (payStationCollectionState.status ===
                                            RequestStatus.SUCCESS &&
                                            !payStationCollectionState.data
                                                .hasCashMovementsReport),
                                    onClickCallback: () => {
                                        openNewTab(
                                            urlWithOperatorExternalId(
                                                `/ui-api/operator-account/pay-station/collections/${year}/${month}/cash-movements/csv`,
                                                operatorExternalId,
                                            ),
                                        );
                                    },
                                },
                            ]}
                        />
                        <Button
                            variant="outlined"
                            onClick={() => navigate('../pay-station-statement')}
                        >
                            <Localized
                                de="Schliessen"
                                fr="Fermer"
                                it="Chiudi"
                                en="Close"
                            />
                        </Button>
                    </Stack>
                }
            />
            <SeparationBar />
            <OperatorAsyncLoadedSection
                requestState={payStationCollectionState}
                pendingLoaderPreset={
                    LoadingSpinnerPresets.FillAllSpaceAndCenter
                }
                render={data => {
                    if (data.collections.length === 0) {
                        return <EmptyResults hasFilter={false} />;
                    }
                    return (
                        <TableContainer>
                            <Table stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>
                                            <Localized
                                                de="Leerungs Nr."
                                                fr="No. collecte"
                                                it="Nr. vuotatura"
                                                en="Collection no."
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Localized
                                                de="Datum"
                                                fr="Date"
                                                it="Data"
                                                en="Date"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Localized
                                                de="Parking"
                                                fr="Parking"
                                                it="Parcheggio"
                                                en="Parking"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Localized
                                                de="Kasse"
                                                fr="Caisse"
                                                it="Cassa"
                                                en="Pay station"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Localized
                                                de="Anzahl Transaktionen"
                                                fr="Nombre de transactions"
                                                it="Numero di transazioni"
                                                en="Number of transactions"
                                            />
                                        </TableCell>
                                        <TableCell align="right">
                                            <Localized
                                                de="Betrag (CHF)"
                                                fr="Montant (CHF)"
                                                it="Importo (CHF)"
                                                en="Amount (CHF)"
                                            />
                                        </TableCell>
                                        <TableCell>PDF</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {data.collections.map(collection => (
                                        <PayStationStatementRow
                                            key={collection.collectionNr}
                                            collection={collection}
                                        />
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    );
                }}
            />
        </MainContent>
    );
}

function PayStationStatementRow({
    collection,
}: {
    collection: PayStationCollection;
}) {
    const { operatorExternalId } = useOperatorContext();
    return (
        <TableRow>
            <TableCell>{collection.collectionNr}</TableCell>
            <TableCell>
                {Formatter.dayMonthYearHourMinuteSecond(collection.collectedAt)}
            </TableCell>
            <TableCell>{collection.zoneName}</TableCell>
            <TableCell>{collection.payStationName}</TableCell>
            <TableCell>{collection.transactionsCount}</TableCell>
            <TableCell align="right">
                {currencyCentsToLocalPrice(
                    collection.collectedAmountRappen,
                    false,
                )}
            </TableCell>
            <TableCell>
                {collection.hasPdfReport && (
                    <a
                        target="_blank"
                        href={urlWithOperatorExternalId(
                            `/ui-api/operator-account/pay-station/collection/${collection.collectionId}/pdf`,
                            operatorExternalId,
                        )}
                        rel="noreferrer"
                    >
                        <PictureAsPdf
                            sx={{
                                color: theme => theme.palette.primary.main,
                                '&:hover': {
                                    color: theme => theme.palette.blue.electric,
                                },
                            }}
                        />
                    </a>
                )}
            </TableCell>
        </TableRow>
    );
}
